import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Dialog, Box, Typography, Button, IconButton, CircularProgress, Snackbar, Alert,
  Chip, Tooltip, Accordion, AccordionSummary, AccordionDetails, Divider,
} from '@mui/material'
import {
  X, Save, CheckCircle2, ChevronLeft, ChevronRight, AlertTriangle, RefreshCw,
  ChevronDown, Eye, FileEdit, FileText,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  fetchConsolidationPreview,
  confirmConsolidationPreview,
  closeConsolidationPreview,
  openConsolidationPreview,
  previewSingleConsolidation,
} from '../../../../redux/actions/boConsolidationAction'
import { parseHtmlChunks } from '../../../../components/html-viewer/parseHtmlChunks'
import LazyIframe from '../../../../components/html-viewer/VirtualizedHtml'
import RichHtmlEditor from '../RichHtmlEditor'

/**
 * BOConsolidationPreview — fullscreen 3-pane viewer for previewing
 * an amendment consolidation before final execution.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────┐
 *   │ Header: navigation + actions (Skip / Save / Confirm)   │
 *   ├────────────┬────────────┬───────────────────────────────┤
 *   │ Target     │ Amendment  │ Merged (editable)             │
 *   │ (orig)     │            │                               │
 *   ├────────────┴────────────┴───────────────────────────────┤
 *   │ Article changes strip (chips)                           │
 *   │ Metadata accordion (read-only)                          │
 *   └─────────────────────────────────────────────────────────┘
 */
export default function BOConsolidationPreview() {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const extractedDocs = useSelector((s) => s.boConsolidation?.extractedDocs) || { update: [] }
  const consolidationPreview = useSelector((s) => s.boConsolidation?.consolidationPreview) || {
    bulkLoading: false,
    bulkProgress: { step: '', percent: 0, message: '', current: 0, total: 0 },
    currentDocId: null,
    fetchLoading: false,
    byDocId: {},
    perDocLoading: {},
    perDocError: {},
  }
  const currentDocId = consolidationPreview.currentDocId
  const updateDocs = extractedDocs.update || []
  const previewData = currentDocId ? (consolidationPreview.byDocId || {})[currentDocId] : null
  const isFetching = consolidationPreview.fetchLoading
  const isComputing = currentDocId ? !!(consolidationPreview.perDocLoading || {})[currentDocId] : false
  const computeError = currentDocId ? (consolidationPreview.perDocError || {})[currentDocId] : null

  const [editedHtml, setEditedHtml] = useState('')
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [snackbar, setSnackbar] = useState(null)
  const editedHtmlRef = useRef('')

  const open = currentDocId !== null

  // Find current doc + index in the update list (only docs that have been
  // approved or further along — these are the only ones the user is reviewing)
  const eligibleDocs = useMemo(
    () => updateDocs.filter((d) =>
      ['approved', 'previewed', 'preview_failed', 'confirmed'].includes(d.validation_status)
    ),
    [updateDocs]
  )
  const currentIndex = eligibleDocs.findIndex((d) => d.id === currentDocId)
  const currentDoc = currentIndex >= 0 ? eligibleDocs[currentIndex] : null

  // Fetch preview when modal opens or when single-preview compute completes
  useEffect(() => {
    if (open && currentDocId && !isComputing) {
      dispatch(fetchConsolidationPreview(currentDocId))
    }
  }, [open, currentDocId, isComputing, dispatch])

  // Reset editor state when switching docs
  useEffect(() => {
    if (previewData) {
      setEditedHtml(previewData.merged_html || '')
      editedHtmlRef.current = previewData.merged_html || ''
      setDirty(false)
    } else {
      setEditedHtml('')
      editedHtmlRef.current = ''
      setDirty(false)
    }
  }, [previewData?.doc_id])

  // Parse HTML for the iframes (target & amendment)
  const targetParsed = useMemo(
    () => parseHtmlChunks(previewData?.target_html || ''),
    [previewData?.target_html]
  )
  const amendmentParsed = useMemo(
    () => parseHtmlChunks(previewData?.amendment_html || ''),
    [previewData?.amendment_html]
  )

  const handleClose = useCallback(() => {
    if (dirty) {
      const ok = window.confirm(t('du_bo_unsaved_changes', 'You have unsaved edits. Close anyway?'))
      if (!ok) return
    }
    dispatch(closeConsolidationPreview())
  }, [dispatch, dirty, t])

  const handleHtmlChange = useCallback((html) => {
    editedHtmlRef.current = html
    setEditedHtml(html)
    setDirty(true)
  }, [])

  const handleConfirm = async () => {
    if (!currentDocId) return
    setSaving(true)
    try {
      await dispatch(
        confirmConsolidationPreview(currentDocId, {
          merged_html: dirty ? editedHtmlRef.current : undefined,
        })
      )
      setSnackbar({ severity: 'success', message: t('du_bo_consolidation_confirmed', 'Consolidation confirmed') })
      setDirty(false)
      // Auto-advance to next doc after a brief delay
      setTimeout(() => {
        const nextDoc = eligibleDocs[currentIndex + 1]
        if (nextDoc) {
          dispatch(openConsolidationPreview(nextDoc.id))
        }
      }, 600)
    } catch (e) {
      setSnackbar({ severity: 'error', message: e.message || 'Confirm failed' })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveEdit = async () => {
    if (!currentDocId || !dirty) return
    setSaving(true)
    try {
      // Save edited HTML without confirming (still in 'previewed' status)
      // We use the same confirm endpoint but won't transition; the backend
      // currently transitions on PUT regardless. To keep semantics simple,
      // Save Edit also confirms.
      await dispatch(
        confirmConsolidationPreview(currentDocId, { merged_html: editedHtmlRef.current })
      )
      setSnackbar({ severity: 'success', message: t('du_bo_edit_saved', 'Edit saved') })
      setDirty(false)
    } catch (e) {
      setSnackbar({ severity: 'error', message: e.message || 'Save failed' })
    } finally {
      setSaving(false)
    }
  }

  const handleSkip = () => {
    const nextDoc = eligibleDocs[currentIndex + 1]
    if (nextDoc) {
      dispatch(openConsolidationPreview(nextDoc.id))
    } else {
      dispatch(closeConsolidationPreview())
    }
  }

  const handlePrev = () => {
    const prev = eligibleDocs[currentIndex - 1]
    if (prev) dispatch(openConsolidationPreview(prev.id))
  }

  const handleNext = () => {
    const next = eligibleDocs[currentIndex + 1]
    if (next) dispatch(openConsolidationPreview(next.id))
  }

  const handleRetryPreview = () => {
    if (currentDocId) dispatch(previewSingleConsolidation(currentDocId))
  }

  // Article chips → scroll merged pane to the corresponding article
  const handleScrollToArticle = (articleNum) => {
    // Trigger an in-iframe scroll. RichHtmlEditor renders into a contentEditable div,
    // not an iframe, so we use querySelector inside its DOM element.
    const editorRoot = document.querySelector('.bo-merged-editor [contenteditable]')
    if (!editorRoot) return
    const target = editorRoot.querySelector(`[data-article="${articleNum}"]`)
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (!open) return null

  const status = currentDoc?.validation_status
  const articleReplacements = previewData?.article_replacements || {}
  const articleNumbers = Object.keys(articleReplacements)
  const isFailed = status === 'preview_failed' || (previewData?.error)
  const isReady = status === 'previewed' || status === 'confirmed'

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: 'background.default', display: 'flex', flexDirection: 'column' } }}
    >
      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <IconButton size="small" onClick={handlePrev} disabled={currentIndex <= 0}>
            <ChevronLeft size={18} />
          </IconButton>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, minWidth: 80 }}>
            {currentIndex + 1} / {eligibleDocs.length}
          </Typography>
          <IconButton size="small" onClick={handleNext} disabled={currentIndex >= eligibleDocs.length - 1}>
            <ChevronRight size={18} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <FileText size={16} color="#c9a96e" />
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
            {currentDoc?.doc_type} {currentDoc?.doc_number}
            {previewData?.target_doc_number && (
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 1 }}>
                → {t('du_bo_amends', 'amends')} {previewData.target_doc_number}
              </Box>
            )}
          </Typography>
          {status && <StatusChip status={status} t={t} />}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            onClick={handleSkip}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t('du_bo_skip', 'Skip')}
          </Button>
          {isReady && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Save size={14} />}
                disabled={!dirty || saving}
                onClick={handleSaveEdit}
                sx={{
                  textTransform: 'none',
                  borderColor: '#c9a96e',
                  color: '#c9a96e',
                  '&:hover': { borderColor: '#b8944f', backgroundColor: 'rgba(201,169,110,0.04)' },
                }}
              >
                {t('du_bo_save_edit', 'Save edit')}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckCircle2 size={14} />}
                disabled={saving || status === 'confirmed'}
                onClick={handleConfirm}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#4caf50',
                  '&:hover': { backgroundColor: '#43a047' },
                  '&.Mui-disabled': { backgroundColor: 'rgba(76,175,80,0.3)', color: 'white' },
                }}
              >
                {status === 'confirmed' ? t('du_bo_confirmed', 'Confirmed') : t('du_bo_confirm', 'Confirm')}
              </Button>
            </>
          )}
          {isFailed && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshCw size={14} />}
              onClick={handleRetryPreview}
              disabled={isComputing}
              sx={{ textTransform: 'none' }}
            >
              {t('du_bo_retry_preview', 'Retry')}
            </Button>
          )}
          <IconButton onClick={handleClose} size="small">
            <X size={18} />
          </IconButton>
        </Box>
      </Box>

      {/* ─── Body ────────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {(isFetching || isComputing) ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <CircularProgress sx={{ color: '#c9a96e' }} />
            <Typography variant="body2" color="text.secondary">
              {isComputing
                ? t('du_bo_computing_preview', 'Computing consolidation preview... this may take 10–30 seconds')
                : t('du_bo_loading_preview', 'Loading preview...')}
            </Typography>
          </Box>
        ) : isFailed ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
            <AlertTriangle size={48} color="#f44336" />
            <Typography variant="h6" sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 600 }}>
              {t('du_bo_preview_failed', 'Preview generation failed')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, textAlign: 'center' }}>
              {previewData?.error || computeError || t('du_bo_preview_failed_default', 'The target document could not be found or the merge could not be computed.')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<RefreshCw size={14} />}
                onClick={handleRetryPreview}
                sx={{ textTransform: 'none', backgroundColor: '#c9a96e', '&:hover': { backgroundColor: '#b8944f' } }}
              >
                {t('du_bo_retry_preview', 'Retry')}
              </Button>
              <Button onClick={handleSkip} sx={{ textTransform: 'none' }}>
                {t('du_bo_skip', 'Skip')}
              </Button>
            </Box>
          </Box>
        ) : previewData ? (
          <>
            {/* 3-pane viewer */}
            <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
              {/* LEFT — Original target */}
              <PanelLabelled
                title={t('du_bo_target_doc', 'Target document (original)')}
                subtitle={previewData.target_title}
                tone="neutral"
              >
                <LazyIframe
                  chunks={targetParsed.chunks}
                  styles={targetParsed.styles}
                  dir={targetParsed.dir || 'ltr'}
                />
              </PanelLabelled>

              {/* MIDDLE — Amendment */}
              <PanelLabelled
                title={t('du_bo_amendment_doc', 'Amendment document')}
                subtitle={`${currentDoc?.doc_type} ${currentDoc?.doc_number || ''}`}
                tone="neutral"
              >
                <LazyIframe
                  chunks={amendmentParsed.chunks}
                  styles={amendmentParsed.styles}
                  dir={amendmentParsed.dir || 'ltr'}
                />
              </PanelLabelled>

              {/* RIGHT — Merged (editable) */}
              <Box
                className="bo-merged-editor"
                sx={{
                  flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
                  borderLeft: 1, borderColor: 'divider', overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', backgroundColor: 'rgba(201,169,110,0.06)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileEdit size={14} color="#c9a96e" />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#c9a96e', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {t('du_bo_merged_doc', 'Merged result (editable)')}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                  <RichHtmlEditor
                    value={editedHtml}
                    onChange={handleHtmlChange}
                    maxHeight="calc(100vh - 280px)"
                  />
                </Box>
              </Box>
            </Box>

            {/* Article changes strip */}
            {articleNumbers.length > 0 && (
              <Box
                sx={{
                  borderTop: 1, borderColor: 'divider', px: 3, py: 1.5,
                  display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mr: 1 }}>
                  {t('du_bo_article_changes', 'Article changes')}:
                </Typography>
                {articleNumbers.map((art) => (
                  <Tooltip key={art} title={t('du_bo_scroll_to_article', 'Scroll to article in merged pane')} arrow>
                    <Chip
                      label={`Art ${art}`}
                      size="small"
                      onClick={() => handleScrollToArticle(art)}
                      icon={<Eye size={12} />}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: 'rgba(201,169,110,0.12)',
                        color: '#c9a96e',
                        fontWeight: 500,
                        '&:hover': { backgroundColor: 'rgba(201,169,110,0.22)' },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            )}

            {/* Metadata accordion */}
            <Accordion
              elevation={0}
              sx={{ borderTop: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ChevronDown size={16} />}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {t('du_bo_updated_metadata', 'Updated document metadata (read-only)')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre
                  style={{
                    fontSize: 11,
                    fontFamily: '"JetBrains Mono", monospace',
                    margin: 0,
                    padding: 12,
                    backgroundColor: '#fafafa',
                    borderRadius: 4,
                    overflow: 'auto',
                    maxHeight: 200,
                  }}
                >
                  {JSON.stringify(previewData.updated_metadata || {}, null, 2)}
                </pre>
              </AccordionDetails>
            </Accordion>
          </>
        ) : null}
      </Box>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar && (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled">
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Dialog>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function PanelLabelled({ title, subtitle, tone, children }) {
  return (
    <Box
      sx={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
        borderRight: 1, borderColor: 'divider', overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2, py: 1, borderBottom: 1, borderColor: 'divider',
          backgroundColor: tone === 'neutral' ? 'rgba(0,0,0,0.02)' : 'rgba(201,169,110,0.06)',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {children}
      </Box>
    </Box>
  )
}

function StatusChip({ status, t }) {
  const config = {
    approved: { label: t('du_bo_status_approved', 'Awaiting preview'), color: 'default' },
    previewed: { label: t('du_bo_status_previewed', 'Preview ready'), color: 'info' },
    preview_failed: { label: t('du_bo_status_preview_failed', 'Preview failed'), color: 'error' },
    confirmed: { label: t('du_bo_status_confirmed', 'Confirmed'), color: 'success' },
  }[status] || { label: status, color: 'default' }

  return <Chip size="small" label={config.label} color={config.color} sx={{ ml: 1, height: 22 }} />
}
